<template>
  <div class="lottery-page">
    <section class="lottery-toolbar" aria-label="抽奖报名筛选">
      <div class="lottery-toolbar__summary">
        <div class="lottery-toolbar__copy">
          <div class="lottery-toolbar__title">抽奖活动审核</div>
          <div class="lottery-toolbar__desc">
            {{ campaignLabel }}<span v-if="campaignWindowText"> · {{ campaignWindowText }}</span>
          </div>
        </div>

        <div class="lottery-metrics" aria-label="报名数据概览">
          <div class="lottery-metric">
            <span>待审核</span>
            <strong class="lottery-metric__pending">{{ stats.pending }}</strong>
          </div>
          <div class="lottery-metric">
            <span>已通过</span>
            <strong>{{ stats.approved }}</strong>
          </div>
          <div class="lottery-metric">
            <span>已驳回</span>
            <strong>{{ stats.rejected }}</strong>
          </div>
          <div class="lottery-metric">
            <span>今日提交</span>
            <strong>{{ stats.todaySubmitted }}</strong>
          </div>
          <div class="lottery-metric">
            <span>全部报名</span>
            <strong>{{ stats.total }}</strong>
          </div>
        </div>
      </div>

      <div class="filter-bar">
        <div class="status-tabs" aria-label="审核状态">
          <button
            v-for="tab in statusTabs"
            :key="tab.value || 'ALL'"
            type="button"
            class="status-tab"
            :class="{ 'status-tab--active': filters.status === tab.value }"
            :aria-pressed="String(filters.status === tab.value)"
            @click="setStatusFilter(tab.value)"
          >
            <span>{{ tab.label }}</span>
            <span class="status-tab__count">{{ tab.count }}</span>
          </button>
        </div>

        <label class="filter-control">
          <span class="sr-only">选择活动</span>
          <select v-model="filters.campaignId" @change="reloadAll">
            <option v-for="item in campaigns" :key="String(item.id)" :value="String(item.id)">
              {{ item.name }}
            </option>
          </select>
        </label>

        <label class="filter-search">
          <input
            v-model.trim="filters.keyword"
            type="search"
            placeholder="搜索昵称、OpenID 或报名编号"
            @keyup.enter="loadEntries"
          />
        </label>

        <button class="ghost-button" type="button" @click="loadEntries">搜索</button>
        <button class="refresh-button" type="button" :disabled="loading" @click="reloadAll">
          {{ loading ? "刷新中" : "刷新" }}
        </button>
      </div>
    </section>

    <section class="draw-panel" aria-label="开奖">
      <div class="draw-panel__head">
        <div>
          <div class="draw-panel__title">开奖</div>
          <div class="draw-panel__desc">
            终极大奖按活动期内参赛帖「点赞 + 评论」热度取第 1 名；其余奖项在审核通过的用户中随机抽取。
          </div>
        </div>
        <div class="draw-panel__actions">
          <span class="draw-fact">已通过报名 <strong>{{ drawState.approvedCount || 0 }}</strong> 人</span>
          <span class="draw-fact" v-if="drawState.drawAt">计划开奖 {{ formatDateTime(drawState.drawAt) }}</span>
          <span v-if="drawState.pendingConfirm" class="draw-flag draw-flag--pending">名单待确认</span>
          <span v-else-if="drawState.confirmed" class="draw-flag draw-flag--done">已开奖公示</span>
          <button
            v-if="drawState.drawn"
            class="ghost-button"
            type="button"
            :disabled="drawing"
            @click="revokeDraw"
          >撤销开奖（活动恢复进行中）</button>
          <button
            v-if="drawState.pendingConfirm"
            class="refresh-button"
            type="button"
            :disabled="drawing"
            @click="confirmDraw"
          >{{ drawing ? "确认中…" : "确认开奖并通知用户" }}</button>
          <button class="refresh-button" type="button" :disabled="drawing" @click="executeDraw">
            {{ drawing ? "开奖中…" : (drawState.drawn ? "重新开奖" : "执行开奖") }}
          </button>
        </div>
      </div>

      <p v-if="drawState.pendingConfirm" class="draw-callout">
        中奖名单已经算出来了，但还没有对用户公示，也没有通知中奖人。请你核对下面的名单，确认无误后点右上角「确认开奖并通知用户」，
        系统才会公示名单并逐个发中奖通知。核对有问题可以点「撤销开奖」重算。
      </p>
      <p v-else-if="drawState.confirmed" class="draw-callout draw-callout--done">
        开奖结果已于 {{ formatDateTime(drawState.confirmedAt) }} 确认，名单已在小程序活动页公示，中奖用户也已收到通知。
      </p>

      <div v-if="drawState.drawn" class="winner-list">
        <div v-for="winner in drawState.winners" :key="String(winner.id)" class="winner-row">
          <span class="winner-level">{{ levelLabel(winner.levelCode) }}</span>
          <span v-if="!winner.confirmed" class="winner-state">待确认</span>
          <span class="winner-avatar">
            <img v-if="winner.avatarUrl" :src="resolveFileUrl(winner.avatarUrl)" alt="" />
            <span v-else>{{ (winner.nickname || "爬").slice(0, 1) }}</span>
          </span>
          <span class="winner-name">{{ winner.nickname || "未设置昵称" }}</span>
          <span class="winner-post">{{ winner.postSummary || "—" }}</span>
          <span v-if="winner.awardMode === 'HOT_RANK'" class="winner-heat">热度 {{ winner.heatScore }} · 第 1 名</span>
          <span v-else class="winner-heat">随机抽取</span>
          <span class="winner-contact" :class="{ 'winner-contact--missing': !winner.receiverComplete }">
            {{ receiverText(winner) }}
          </span>
        </div>
        <p class="draw-note">
          参与人数 {{ drawState.participantsCount || 0 }}，名单指纹 {{ (drawState.participantsHash || "").slice(0, 16) }}…，
          随机种子 {{ drawState.seedValue }}
        </p>
      </div>
      <p v-else class="draw-empty">
        还没有开奖结果。确认报名审核完毕后点「执行开奖」，系统会按规则生成中奖名单并在小程序活动页公示。
      </p>
    </section>

    <section class="lottery-workbench">
      <aside class="queue-panel" aria-label="报名队列">
        <div class="queue-panel__head">
          <div>
            <div class="queue-panel__title">报名队列</div>
            <div class="queue-panel__desc">按提交时间排序，待审核优先</div>
          </div>
          <span class="queue-panel__count">{{ entries.length }}</span>
        </div>

        <div v-if="loading" class="queue-state">正在加载报名记录...</div>

        <div v-else-if="entries.length" class="queue-list">
          <button
            v-for="item in entries"
            :key="String(item.id)"
            type="button"
            class="queue-item"
            :class="{ 'queue-item--active': selectedEntry && selectedEntry.id === item.id }"
            @click="selectEntry(item)"
          >
            <span class="queue-item__avatar">
              <img v-if="item.avatarUrl" :src="resolveFileUrl(item.avatarUrl)" alt="" />
              <span v-else>{{ nickInitial(item) }}</span>
            </span>
            <span class="queue-item__content">
              <span class="queue-item__topline">
                <strong class="queue-item__name">{{ item.nickname || "未设置昵称" }}</strong>
                <span class="status-dot" :class="resolveStatusDotClass(item.status)"></span>
              </span>
              <span class="queue-item__post">{{ postSummary(item) }}</span>
              <span class="queue-item__bottomline">
                <span>#{{ item.id }}</span>
                <span>{{ formatDateTime(item.submittedAt) }}</span>
              </span>
            </span>
          </button>
        </div>

        <div v-else class="queue-empty">
          <div class="queue-empty__mark">0</div>
          <strong>没有匹配的报名</strong>
          <span>调整状态或搜索条件后再试。</span>
        </div>
      </aside>

      <article v-if="selectedEntry" class="entry-detail">
        <header class="detail-header">
          <div class="detail-header__account">
            <div
              class="account-card__avatar"
              :class="{ 'account-card__avatar--zoom': selectedEntry.avatarUrl }"
              @click="previewEntryAvatar(selectedEntry)"
            >
              <img v-if="selectedEntry.avatarUrl" :src="resolveFileUrl(selectedEntry.avatarUrl)" alt="" />
              <span v-else>{{ nickInitial(selectedEntry) }}</span>
            </div>
            <div class="detail-header__copy">
              <div class="detail-header__eyebrow">报名 #{{ selectedEntry.id }} · UID {{ selectedEntry.userId }}</div>
              <div class="detail-header__title-row">
                <h2>{{ selectedEntry.nickname || "未设置昵称" }}</h2>
                <span class="status-pill" :class="resolveStatusClass(selectedEntry.status)">
                  {{ statusLabel(selectedEntry.status) }}
                </span>
              </div>
              <div class="detail-header__subline">
                <span class="detail-header__openid">OpenID {{ selectedEntry.openId || "-" }}</span>
                <span
                  class="bind-badge"
                  :class="selectedEntry.officialBound ? 'bind-badge--on' : 'bind-badge--off'"
                >
                  {{ selectedEntry.officialBound ? "已绑定服务号" : "未绑定服务号" }}
                </span>
              </div>
            </div>
          </div>
          <div class="detail-header__meta">
            <span>提交于 {{ formatDateTime(selectedEntry.submittedAt) }}</span>
            <span v-if="selectedEntry.reviewedAt">
              审核于 {{ formatDateTime(selectedEntry.reviewedAt) }} · {{ selectedEntry.reviewedBy || "-" }}
            </span>
          </div>
        </header>

        <div class="detail-scroll">
          <div class="detail-columns">
            <div class="detail-content">
              <section class="detail-section">
                <div class="section-heading">
                  <div>
                    <h3>参赛帖</h3>
                    <p>报名时绑定的广场帖子</p>
                  </div>
                  <span v-if="selectedEntry.postId" class="risk-summary">
                    {{ selectedEntry.likeCount || 0 }} 赞 · {{ selectedEntry.commentCount || 0 }} 评论
                  </span>
                </div>
                <div v-if="selectedEntry.postId" class="post-card">
                  <img
                    v-if="selectedEntry.postImageUrl"
                    class="post-card__image"
                    :src="resolveFileUrl(selectedEntry.postImageUrl)"
                    alt=""
                  />
                  <div class="post-card__body">
                    <div class="post-card__id">帖子 #{{ selectedEntry.postId }}</div>
                    <p>{{ selectedEntry.postContent || "这条帖子只有图片，没有文字内容" }}</p>
                  </div>
                </div>
                <div v-else class="detail-empty-line">该报名未绑定参赛帖，请确认是否需要驳回。</div>
              </section>

              <section class="detail-section">
                <div class="section-heading">
                  <div>
                    <h3>收货信息</h3>
                    <p>奖品需要邮寄，信息缺失的中奖用户无法发货</p>
                  </div>
                  <span
                    class="risk-summary"
                    :class="{ 'risk-summary--risk': !entryReceiverComplete }"
                  >
                    {{ entryReceiverComplete ? "已填齐" : "缺失" }}
                  </span>
                </div>
                <dl class="info-grid">
                  <div>
                    <dt>收货人</dt>
                    <dd>{{ selectedEntry.receiverName || "—" }}</dd>
                  </div>
                  <div>
                    <dt>联系电话</dt>
                    <dd>{{ selectedEntry.receiverPhone || "—" }}</dd>
                  </div>
                  <div class="info-grid__wide">
                    <dt>收货地址</dt>
                    <dd>{{ selectedEntry.receiverAddress || "—" }}</dd>
                  </div>
                </dl>
              </section>

              <section class="detail-section">
                <div class="section-heading">
                  <div>
                    <h3>系统预检</h3>
                    <p>报名时自动记录的校验信息</p>
                  </div>
                </div>
                <div class="precheck-box">{{ selectedEntry.precheckResult || "无预检记录" }}</div>
              </section>
            </div>

            <aside class="decision-panel">
              <div class="section-heading">
                <div>
                  <h3>审核结论</h3>
                  <p>结论会通过站内消息通知用户</p>
                </div>
              </div>

              <label class="decision-field">
                <span>审核备注 <small>选填</small></span>
                <textarea v-model.trim="decisionForm.remark" placeholder="记录判断依据，便于后续复核" />
              </label>

              <label class="decision-field">
                <span>驳回理由 <small>驳回时必填</small></span>
                <textarea
                  v-model.trim="decisionForm.reason"
                  class="decision-field__textarea--reject"
                  placeholder="例如：头像昵称与微信不一致，请调整后重新提交"
                />
              </label>

              <div class="reason-presets">
                <button
                  v-for="reason in rejectReasonPresets"
                  :key="reason"
                  type="button"
                  @click="decisionForm.reason = reason"
                >
                  {{ reason }}
                </button>
              </div>

              <div v-if="selectedEntry.status !== 'PENDING'" class="reopen-hint">
                重新审核会覆盖当前结论，并再次通知用户。
              </div>
            </aside>
          </div>
        </div>

        <footer class="decision-bar">
          <div class="decision-bar__state">
            <template v-if="actionState">
              <span class="decision-bar__indicator" :class="{ 'decision-bar__indicator--error': actionError }"></span>
              <span :class="{ 'decision-bar__text--error': actionError }">{{ actionState }}</span>
            </template>
            <template v-else>
              <span>审核通过后用户即进入抽奖名单</span>
            </template>
          </div>
          <div class="decision-bar__actions">
            <button
              class="decision-button decision-button--reject"
              type="button"
              :disabled="submitting || selectedEntry.status === 'REJECTED'"
              @click="rejectCurrent"
            >
              {{ submitting && actionType === "reject" ? "提交中..." : "驳回报名" }}
            </button>
            <button
              class="decision-button decision-button--approve"
              type="button"
              :disabled="submitting || selectedEntry.status === 'APPROVED'"
              @click="approveCurrent"
            >
              {{ submitting && actionType === "approve" ? "提交中..." : "通过审核" }}
            </button>
          </div>
        </footer>
      </article>

      <div v-else class="entry-detail entry-detail--empty">
        <div class="detail-empty__mark">抽</div>
        <h2>选择一条报名开始审核</h2>
        <p>左侧队列会保留当前筛选条件。</p>
      </div>
    </section>

    <div v-if="previewImage" class="image-preview" @click="previewImage = ''">
      <img :src="previewImage" alt="图片预览" />
      <span class="image-preview__hint">点击任意位置关闭</span>
    </div>
  </div>
</template>

<script>
import { resolveBackendAssetUrl } from "../utils/apiEndpoint"
import {
  fetchActivityCampaigns,
  fetchActivityEntries,
  fetchActivityStats,
  confirmActivityDraw,
  fetchDrawState,
  revokeActivityDraw,
  runActivityDraw,
  reviewActivityEntry
} from "../api/activity"

function emptyStats() {
  return {
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    todaySubmitted: 0
  }
}

export default {
  name: "ActivityLotteryView",
  data() {
    return {
      loading: false,
      submitting: false,
      actionType: "",
      actionState: "",
      actionError: false,
      campaigns: [],
      campaign: null,
      stats: emptyStats(),
      drawState: { approvedCount: 0, drawn: false, winners: [] },
      drawing: false,
      entries: [],
      selectedEntry: null,
      previewImage: "",
      filters: {
        campaignId: "",
        status: "PENDING",
        keyword: ""
      },
      decisionForm: {
        remark: "",
        reason: ""
      },
      rejectReasonPresets: [
        "头像昵称与微信不一致",
        "收货信息填写不完整",
        "帖子内容非真实养宠日常",
        "疑似搬运或水帖",
        "未按活动要求发布"
      ]
    }
  },
  computed: {
    entryReceiverComplete() {
      const entry = this.selectedEntry
      if (!entry) return false
      return Boolean(entry.receiverName && entry.receiverPhone && entry.receiverAddress)
    },
    campaignLabel() {
      return this.campaign ? this.campaign.name : "暂无进行中的活动"
    },
    campaignWindowText() {
      if (!this.campaign) {
        return ""
      }
      const open = this.formatDate(this.campaign.entryOpenAt)
      const close = this.formatDate(this.campaign.entryCloseAt)
      const draw = this.formatDate(this.campaign.drawAt)
      return `报名 ${open} ~ ${close}｜开奖 ${draw}`
    },
    statusTabs() {
      return [
        { label: "待审核", value: "PENDING", count: this.stats.pending },
        { label: "已通过", value: "APPROVED", count: this.stats.approved },
        { label: "已驳回", value: "REJECTED", count: this.stats.rejected },
        { label: "全部", value: "", count: this.stats.total }
      ]
    }
  },
  created() {
    this.loadCampaigns()
  },
  methods: {
    async loadCampaigns() {
      try {
        const res = await fetchActivityCampaigns()
        this.campaigns = Array.isArray(res.data) ? res.data : []
      } catch (error) {
        this.campaigns = []
      }
      if (this.campaigns.length) {
        this.filters.campaignId = String(this.campaigns[0].id)
        this.campaign = this.campaigns[0]
      }
      await this.reloadAll()
    },
    async reloadAll() {
      this.campaign = this.campaigns.find(item => String(item.id) === String(this.filters.campaignId)) || null
      await Promise.all([this.loadStats(), this.loadEntries(), this.loadDrawState()])
    },
    async loadDrawState() {
      try {
        const res = await fetchDrawState({ campaignId: this.filters.campaignId || undefined })
        this.drawState = res.data || { approvedCount: 0, drawn: false, winners: [] }
      } catch (error) {
        this.drawState = { approvedCount: 0, drawn: false, winners: [] }
      }
    },
    async executeDraw() {
      if (this.drawing) return
      if (this.drawState.drawn) {
        if (!window.confirm("重新开奖会覆盖当前中奖名单（已公示、已通知的也会被覆盖），确认继续？")) return
      } else if (!window.confirm("按当前审核通过的名单算出中奖结果？这一步不会通知用户，你核对后还需要点「确认开奖并通知用户」。")) {
        return
      }
      this.drawing = true
      try {
        const res = await runActivityDraw(this.filters.campaignId || undefined)
        this.drawState = res.data || this.drawState
        this.actionState = res.message || "已生成中奖名单，等待你确认"
        this.actionError = false
      } catch (error) {
        this.actionState = (error && error.message) || "开奖失败"
        this.actionError = true
      } finally {
        this.drawing = false
      }
    },
    async confirmDraw() {
      if (this.drawing) return
      const winnerCount = (this.drawState.winners || []).length
      if (!window.confirm(`确认开奖？确认后 ${winnerCount} 位中奖名单会立刻在小程序活动页公示，并给中奖用户发通知，不能再悄悄改。`)) {
        return
      }
      this.drawing = true
      try {
        const res = await confirmActivityDraw(this.filters.campaignId || undefined)
        this.drawState = res.data || this.drawState
        this.actionState = res.message || "已确认开奖"
        this.actionError = false
      } catch (error) {
        this.actionState = (error && error.message) || "确认开奖失败"
        this.actionError = true
      } finally {
        this.drawing = false
      }
    },
    async revokeDraw() {
      if (this.drawing) return
      if (!window.confirm("撤销后中奖名单会被清空，可以重新开奖，确认撤销？")) return
      this.drawing = true
      try {
        await revokeActivityDraw(this.filters.campaignId || undefined)
        await this.loadDrawState()
        this.actionState = "已撤销开奖结果"
        this.actionError = false
      } catch (error) {
        this.actionState = (error && error.message) || "撤销失败"
        this.actionError = true
      } finally {
        this.drawing = false
      }
    },
    levelLabel(levelCode) {
      if (levelCode === "GRAND") return "终极大奖"
      if (levelCode === "FIRST") return "一等奖"
      if (levelCode === "SECOND") return "二等奖"
      if (levelCode === "THIRD") return "三等奖"
      return "奖品"
    },
    receiverText(winner) {
      if (!winner) return "—"
      const name = winner.receiverName || ""
      const phone = winner.receiverPhone || ""
      const address = winner.receiverAddress || ""
      if (!name && !phone && !address) return "收货信息缺失，无法发货"
      return `${name} ${phone}　${address}`.trim()
    },
    async loadStats() {
      try {
        const res = await fetchActivityStats({ campaignId: this.filters.campaignId || undefined })
        const data = res.data || {}
        this.stats = {
          total: Number(data.total || 0),
          pending: Number(data.pending || 0),
          approved: Number(data.approved || 0),
          rejected: Number(data.rejected || 0),
          todaySubmitted: Number(data.todaySubmitted || 0)
        }
        if (data.campaignName && !this.campaign) {
          this.campaign = { id: data.campaignId, name: data.campaignName }
        }
      } catch (error) {
        this.stats = emptyStats()
      }
    },
    async loadEntries() {
      this.loading = true
      try {
        const res = await fetchActivityEntries({
          campaignId: this.filters.campaignId || undefined,
          status: this.filters.status || undefined,
          keyword: this.filters.keyword || undefined,
          page: 1,
          pageSize: 50
        })
        const data = res.data || {}
        this.entries = Array.isArray(data.records) ? data.records : []
        const stillVisible = this.entries.some(item => this.selectedEntry && item.id === this.selectedEntry.id)
        if (!stillVisible) {
          this.selectedEntry = this.entries.length ? this.entries[0] : null
          this.resetDecisionForm()
        }
      } catch (error) {
        this.entries = []
        this.selectedEntry = null
      } finally {
        this.loading = false
      }
    },
    setStatusFilter(status) {
      this.filters.status = status
      this.loadEntries()
    },
    selectEntry(item) {
      this.selectedEntry = item
      this.resetDecisionForm()
      this.actionState = ""
      this.actionError = false
    },
    resetDecisionForm() {
      this.decisionForm = { remark: "", reason: "" }
    },
    async approveCurrent() {
      await this.submitReview("APPROVE")
    },
    async rejectCurrent() {
      await this.submitReview("REJECT")
    },
    async submitReview(action) {
      if (!this.selectedEntry || this.submitting) {
        return
      }
      if (action === "REJECT" && !this.decisionForm.reason) {
        this.actionState = "驳回时必须填写理由"
        this.actionError = true
        return
      }
      this.submitting = true
      this.actionType = action === "APPROVE" ? "approve" : "reject"
      this.actionState = ""
      this.actionError = false
      try {
        await reviewActivityEntry(this.selectedEntry.id, {
          action,
          reason: this.decisionForm.reason || undefined,
          remark: this.decisionForm.remark || undefined
        })
        this.actionState = action === "APPROVE" ? "已通过审核" : "已驳回报名"
        this.actionError = false
        const currentId = this.selectedEntry.id
        await Promise.all([this.loadStats(), this.loadEntries()])
        const next = this.entries.find(item => item.id !== currentId)
        this.selectedEntry = next || null
        this.resetDecisionForm()
      } catch (error) {
        this.actionState = (error && error.message) || "提交失败，请稍后重试"
        this.actionError = true
      } finally {
        this.submitting = false
        this.actionType = ""
      }
    },
    resolveFileUrl(value) {
      return resolveBackendAssetUrl(value)
    },
    resolveStatusClass(status) {
      if (status === "APPROVED") return "status-pill--approved"
      if (status === "REJECTED") return "status-pill--rejected"
      return "status-pill--pending"
    },
    resolveStatusDotClass(status) {
      if (status === "APPROVED") return "status-dot--approved"
      if (status === "REJECTED") return "status-dot--rejected"
      return "status-dot--pending"
    },
    statusLabel(status) {
      if (status === "APPROVED") return "已通过"
      if (status === "REJECTED") return "已驳回"
      return "待审核"
    },
    nickInitial(item) {
      const name = (item && item.nickname) || ""
      return name ? name.slice(0, 1).toUpperCase() : "爬"
    },
    previewEntryAvatar(item) {
      const avatarUrl = item && item.avatarUrl
      if (!avatarUrl) return
      this.previewImage = this.resolveFileUrl(avatarUrl)
    },
    postSummary(item) {
      const content = (item && item.postContent) || ""
      if (!content) return item && item.postId ? "图片帖 · 无文字内容" : "未绑定参赛帖"
      return content.length > 28 ? `${content.slice(0, 28)}…` : content
    },
    formatDate(value) {
      if (!value) return "-"
      return String(value).replace("T", " ").slice(0, 10)
    },
    formatDateTime(value) {
      if (!value) return "-"
      return String(value).replace("T", " ").slice(0, 16)
    }
  }
}
</script>

<style scoped>
.lottery-page {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-height: 100%;
  color: #17221d;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.lottery-toolbar {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 22px;
  background: #fff;
  border: 1px solid #e5ebe7;
  border-radius: 16px;
  box-shadow: 0 10px 26px rgba(23, 34, 29, 0.05);
}

.lottery-toolbar__summary {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}

.lottery-toolbar__title {
  font-size: 20px;
  font-weight: 700;
}

.lottery-toolbar__desc {
  margin-top: 6px;
  color: #6c7a73;
  font-size: 13px;
  line-height: 1.6;
}

.lottery-metrics {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.lottery-metric {
  min-width: 84px;
  padding: 10px 14px;
  text-align: center;
  background: #f5f8f6;
  border-radius: 12px;
}

.lottery-metric span {
  display: block;
  color: #7a8781;
  font-size: 12px;
}

.lottery-metric strong {
  display: block;
  margin-top: 4px;
  font-size: 20px;
  font-weight: 700;
  color: #1f2d26;
}

.lottery-metric__pending {
  color: #d08700 !important;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.status-tabs {
  display: flex;
  gap: 6px;
  padding: 4px;
  background: #f2f5f3;
  border-radius: 12px;
}

.status-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #5d6b64;
  font-size: 13px;
  cursor: pointer;
}

.status-tab--active {
  background: #fff;
  color: #12301f;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(23, 34, 29, 0.08);
}

.status-tab__count {
  color: #8d9a93;
  font-size: 12px;
}

.filter-control select,
.filter-search input {
  height: 36px;
  padding: 0 12px;
  border: 1px solid #dfe6e1;
  border-radius: 10px;
  background: #fff;
  color: #1f2d26;
  font-size: 13px;
  outline: none;
}

.filter-search input {
  width: 240px;
}

.refresh-button,
.ghost-button {
  height: 36px;
  padding: 0 16px;
  border-radius: 10px;
  font-size: 13px;
  cursor: pointer;
}

.refresh-button {
  border: 0;
  background: #0fbd68;
  color: #fff;
}

.refresh-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ghost-button {
  border: 1px solid #dfe6e1;
  background: #fff;
  color: #33463b;
}

.draw-panel {
  padding: 18px 22px;
  background: #fff;
  border: 1px solid #e5ebe7;
  border-radius: 16px;
  box-shadow: 0 10px 26px rgba(23, 34, 29, 0.05);
}

.draw-panel__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
}

.draw-panel__title {
  font-size: 16px;
  font-weight: 700;
}

.draw-panel__desc {
  margin-top: 6px;
  max-width: 620px;
  color: #6c7a73;
  font-size: 12.5px;
  line-height: 1.7;
}

.draw-panel__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.draw-fact {
  color: #6c7a73;
  font-size: 12.5px;
}

.draw-fact strong {
  color: #17221d;
  font-size: 14px;
}

.winner-list {
  margin-top: 16px;
  border-top: 1px solid #eef2ef;
}

.winner-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f4f7f5;
}

.winner-level {
  flex: 0 0 64px;
  color: #6c7a73;
  font-size: 12.5px;
}

.winner-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border-radius: 50%;
  overflow: hidden;
  background: #e8f5ec;
  color: #1f7a49;
  font-size: 13px;
  font-weight: 600;
}

.winner-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.winner-name {
  flex: 0 0 auto;
  min-width: 90px;
  font-size: 14px;
  font-weight: 600;
}

.winner-post {
  flex: 1 1 auto;
  min-width: 0;
  color: #6c7a73;
  font-size: 12.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.winner-heat {
  flex: 0 0 auto;
  color: #86928c;
  font-size: 12px;
}

.winner-contact {
  flex: 0 0 auto;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #33463b;
  font-size: 12px;
}

.winner-contact--missing {
  color: #b23f2c;
  font-weight: 600;
}

.draw-note {
  margin: 12px 0 0;
  color: #86928c;
  font-size: 12px;
  line-height: 1.7;
  word-break: break-all;
}

.draw-empty {
  margin: 16px 0 0;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f7faf8;
  color: #4d5c54;
  font-size: 12.5px;
  line-height: 1.7;
}

.draw-flag {
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.draw-flag--pending {
  background: #fff6e2;
  color: #a86f00;
}

.draw-flag--done {
  background: #e8f8ef;
  color: #14794a;
}

.draw-callout {
  margin: 14px 0 0;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #f2e2bc;
  background: #fffbf0;
  color: #7a5a10;
  font-size: 12.5px;
  line-height: 1.8;
}

.draw-callout--done {
  border-color: #cdebd9;
  background: #f1faf5;
  color: #14794a;
}

.winner-state {
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff6e2;
  color: #a86f00;
  font-size: 11px;
  white-space: nowrap;
}

.lottery-workbench {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.queue-panel,
.entry-detail {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e5ebe7;
  border-radius: 16px;
  box-shadow: 0 10px 26px rgba(23, 34, 29, 0.05);
}

.queue-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid #eef2ef;
}

.queue-panel__title {
  font-size: 15px;
  font-weight: 600;
}

.queue-panel__desc {
  margin-top: 4px;
  color: #86928c;
  font-size: 12px;
}

.queue-panel__count {
  padding: 4px 10px;
  background: #f2f5f3;
  border-radius: 999px;
  color: #4d5c54;
  font-size: 12px;
}

.queue-list {
  max-height: 62vh;
  overflow-y: auto;
  padding: 8px;
}

.queue-item {
  display: flex;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.queue-item:hover {
  background: #f7faf8;
}

.queue-item--active {
  background: #eefaf2;
}

.queue-item__avatar,
.account-card__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border-radius: 12px;
  overflow: hidden;
  background: #e8f5ec;
  color: #1f7a49;
  font-weight: 600;
}

.queue-item__avatar img,
.account-card__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.queue-item__content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.queue-item__topline {
  display: flex;
  align-items: center;
  gap: 8px;
}

.queue-item__name {
  font-size: 14px;
  font-weight: 600;
}

.queue-item__post {
  color: #6f7d76;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.queue-item__bottomline {
  display: flex;
  justify-content: space-between;
  color: #97a29c;
  font-size: 12px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 8px;
}

.status-dot--pending {
  background: #f0a90c;
}

.status-dot--approved {
  background: #0fbd68;
}

.status-dot--rejected {
  background: #d1503f;
}

.queue-state,
.queue-empty {
  padding: 40px 20px;
  text-align: center;
  color: #86928c;
  font-size: 13px;
}

.queue-empty {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.queue-empty__mark {
  font-size: 26px;
  font-weight: 700;
  color: #cdd8d2;
}

.entry-detail {
  min-width: 0;
}

.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  border-bottom: 1px solid #eef2ef;
  flex-wrap: wrap;
}

.detail-header__eyebrow {
  color: #86928c;
  font-size: 12px;
}

.detail-header__account {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.detail-header__subline {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.detail-header__openid {
  color: #97a29c;
  font-size: 12px;
  word-break: break-all;
}

.detail-header__title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
}

.detail-header__title-row h2 {
  margin: 0;
  font-size: 20px;
}

.detail-header__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #86928c;
  font-size: 12px;
  text-align: right;
}

.status-pill {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-pill--pending {
  background: #fff6e2;
  color: #a86f00;
}

.status-pill--approved {
  background: #e8f8ef;
  color: #14794a;
}

.status-pill--rejected {
  background: #fdeeea;
  color: #b23f2c;
}

.detail-scroll {
  padding: 20px 22px;
}

.detail-columns {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 20px;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

.detail-section {
  padding: 16px 18px;
  border: 1px solid #eef2ef;
  border-radius: 14px;
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-heading h3 {
  margin: 0;
  font-size: 15px;
}

.section-heading p {
  margin: 4px 0 0;
  color: #86928c;
  font-size: 12px;
}

.risk-summary {
  padding: 4px 10px;
  background: #f2f5f3;
  border-radius: 999px;
  color: #4d5c54;
  font-size: 12px;
  white-space: nowrap;
}

.risk-summary--risk {
  background: #fdeeea;
  color: #b23f2c;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px 18px;
  margin: 0;
}

.info-grid > div {
  min-width: 0;
}

.info-grid__wide {
  grid-column: 1 / -1;
}

.info-grid dt {
  color: #86928c;
  font-size: 12px;
}

.info-grid dd {
  margin: 5px 0 0;
  color: #1f2d26;
  font-size: 13.5px;
  word-break: break-all;
}

.account-card__avatar--zoom {
  cursor: zoom-in;
}

.bind-badge {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  white-space: nowrap;
}

.bind-badge--on {
  background: #e8f8ef;
  color: #14794a;
}

.bind-badge--off {
  background: #f5f6f5;
  color: #7d8a84;
}

.post-card {
  display: flex;
  gap: 14px;
}

.post-card__image {
  width: 96px;
  height: 96px;
  flex: 0 0 96px;
  object-fit: cover;
  border-radius: 10px;
  background: #f2f5f3;
}

.post-card__id {
  color: #86928c;
  font-size: 12px;
}

.post-card__body p {
  margin: 6px 0 0;
  color: #33463b;
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}

.precheck-box,
.detail-empty-line {
  padding: 12px 14px;
  background: #f7faf8;
  border-radius: 10px;
  color: #4d5c54;
  font-size: 13px;
  line-height: 1.7;
}

.decision-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px;
  border: 1px solid #eef2ef;
  border-radius: 14px;
  background: #fafcfb;
  align-self: flex-start;
  position: sticky;
  top: 8px;
}

.decision-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: #33463b;
}

.decision-field small {
  color: #97a29c;
  font-size: 12px;
}

.decision-field textarea {
  min-height: 96px;
  padding: 10px 12px;
  border: 1px solid #dfe6e1;
  border-radius: 10px;
  background: #fff;
  font-size: 13px;
  line-height: 1.6;
  resize: vertical;
  outline: none;
}

.decision-field__textarea--reject {
  min-height: 132px;
}

.reason-presets {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.reason-presets button {
  padding: 5px 10px;
  border: 1px solid #dfe6e1;
  border-radius: 999px;
  background: #fff;
  color: #4d5c54;
  font-size: 12px;
  cursor: pointer;
}

.reason-presets button:hover {
  border-color: #0fbd68;
  color: #14794a;
}

.reopen-hint {
  padding: 10px 12px;
  background: #fff6e2;
  border-radius: 10px;
  color: #a86f00;
  font-size: 12px;
  line-height: 1.6;
}

.decision-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 22px;
  border-top: 1px solid #eef2ef;
  flex-wrap: wrap;
  position: sticky;
  bottom: 0;
  z-index: 3;
  background: #fff;
  border-radius: 0 0 16px 16px;
}

.decision-bar__state {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6c7a73;
  font-size: 13px;
}

.decision-bar__indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #0fbd68;
}

.decision-bar__indicator--error {
  background: #d1503f;
}

.decision-bar__text--error {
  color: #b23f2c;
}

.decision-bar__actions {
  display: flex;
  gap: 10px;
}

.decision-button {
  height: 42px;
  padding: 0 24px;
  border-radius: 10px;
  font-size: 14px;
  cursor: pointer;
}

.decision-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.decision-button--reject {
  border: 1px solid #f0cdc5;
  background: #fff;
  color: #b23f2c;
}

.decision-button--approve {
  border: 0;
  background: #0fbd68;
  color: #fff;
}

.entry-detail--empty {
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #86928c;
}

.detail-empty__mark {
  font-size: 30px;
  font-weight: 700;
  color: #cdd8d2;
}

.entry-detail--empty h2 {
  margin: 0;
  color: #33463b;
  font-size: 17px;
}

.entry-detail--empty p {
  margin: 0;
  font-size: 13px;
}

.image-preview {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px;
  background: rgba(12, 22, 17, 0.82);
  cursor: zoom-out;
}

.image-preview img {
  max-width: 92vw;
  max-height: 84vh;
  object-fit: contain;
  border-radius: 12px;
  background: #fff;
}

.image-preview__hint {
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;
}

@media (max-width: 1180px) {
  .lottery-workbench {
    grid-template-columns: 1fr;
  }

  .detail-columns {
    grid-template-columns: 1fr;
  }

  .decision-panel {
    position: static;
  }
}
</style>
